import { useMemo } from "react";
import { Breakpoints, useBreakpoints } from "./use-breakpoints";

type BreakpointKeys = Exclude<keyof Breakpoints, "default">;

type Overrides<T> = Partial<Record<BreakpointKeys, T>>;

const breakpointsSmallestToLargest: BreakpointKeys[] = ["xs", "sm", "md", "lg"];

export const useBreakpointValue = <T>(
  defaultValue: T,
  overrides: Overrides<T>
): T => {
  const breakpoints = useBreakpoints();

  return useMemo(() => {
    const values = breakpointsSmallestToLargest.reduce(
      (acc, breakpoint, i) => {
        const value =
          overrides[breakpoint] ??
          acc[breakpointsSmallestToLargest[i - 1]] ??
          defaultValue;

        return {
          ...acc,
          [breakpoint]: value,
        };
      },
      { default: defaultValue } as BreakpointValues<T>
    );

    return breakpointValue(values, breakpoints) ?? defaultValue;
  }, [breakpoints, defaultValue, overrides]);
};

export type BreakpointValues<T> = { [key in keyof Breakpoints]?: T };

/**
 * Returns a value for the largest active breakpoint.
 *
 * returns undefined if no value could be matched
 */
function breakpointValue<T>(
  values: BreakpointValues<T>,
  breakpoints: Breakpoints
): T | undefined {
  // ordered from largest to smallest
  const breakpointsLargestToSmallest = [
    "lg",
    "md",
    "sm",
    "xs",
    "default",
  ] as const;
  const largestActiveBreakpointKey = breakpointsLargestToSmallest.find(
    (breakpoint) => breakpoints[breakpoint]
  );
  if (!largestActiveBreakpointKey) return;

  const valueForBreakpoint = (breakpoint: keyof Breakpoints): T | undefined => {
    // try to find value for a given breakpoint
    if (breakpoint in values) return values[breakpoint];

    const currentBreakpointIndex =
      breakpointsLargestToSmallest.indexOf(breakpoint);
    // if no value defined for a given breakpoint -
    // try next smaller breakpoint
    const smallerBreakpointToTry =
      breakpointsLargestToSmallest[currentBreakpointIndex + 1];

    if (!smallerBreakpointToTry) return;

    return valueForBreakpoint(smallerBreakpointToTry);
  };

  return valueForBreakpoint(largestActiveBreakpointKey);
}
